import React, { useState } from 'react';
import { submitLetterRequest } from '../lib/dataService';
import { toast } from 'sonner';
import { Send, FileText, ShieldCheck } from 'lucide-react';
import '../components/registration.css';

const PURPOSES = [
  'Official Leave',
  'Government Grant Support Request',
  'Letter of Support for Overseas School Application',
];

export default function LetterRequestPage() {
  const [form, setForm] = useState({
    requestor_name: '', requestor_email: '', purpose: '',
    organization: '', recipient_person: '', event_details: '',
  });
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const required = [
      ['requestor_name', 'Requestor name'],
      ['requestor_email', 'Requestor email'],
      ['purpose', 'Purpose of letter'],
      ['organization', 'Organization name and address'],
      ['recipient_person', 'Name and title of the person the letter is being sent to'],
      ['event_details', 'Name and dates of the event'],
    ];
    for (const [key, label] of required) {
      if (!form[key].trim()) { toast.error(`${label} is required.`); return; }
    }
    setSending(true);
    try {
      await submitLetterRequest(form);
      setSubmitted(true);
      toast.success('Letter request submitted! A copy has been emailed to you.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to submit request. Please try again.');
    }
    setSending(false);
  };

  return (
    <div className="static-page">
      <div className="page-hero">
        <div className="container">
          <span className="page-hero-label">Official Documents</span>
          <h1>Letter Requests</h1>
          <p>Request an official letter from the Cayman Islands Basketball Association.</p>
        </div>
      </div>

      <div className="container article-body-wrap section">
        {submitted ? (
          <div className="reg-success">
            <ShieldCheck size={44} />
            <h3>Request Received!</h3>
            <p>Thank you, <strong>{form.requestor_name}</strong>. Your letter request has been sent to CIBA and a copy has been emailed to <strong>{form.requestor_email}</strong>.</p>
            <p>We will process your request and be in touch soon.</p>
          </div>
        ) : (
          <>
            <div className="reg-header">
              <h2>Official Letter Request Form</h2>
              <p>
                If you are requesting a letter from the Cayman Islands Basketball Association, please note that
                the form must be filled out completely. The highlighted fields below show where your information
                needs to be entered — all fields are required <span className="reg-req">*</span>.
              </p>
            </div>
            <form onSubmit={handleSubmit}>
              <section className="reg-card">
                <h3><FileText size={19} /> Request Details</h3>
                <div className="reg-grid reg-grid-2">
                  <label>Requestor (Who is this letter on behalf of)? <span className="reg-req">*</span>
                    <input type="text" className="reg-input" value={form.requestor_name} onChange={set('requestor_name')} placeholder="E.g. John Doe" required />
                  </label>
                  <label>Requestor Email <span className="reg-req">*</span>
                    <input type="email" className="reg-input" value={form.requestor_email} onChange={set('requestor_email')} required />
                  </label>
                </div>
                <div className="reg-grid" style={{ marginTop: 16 }}>
                  <label>Purpose of Letter <span className="reg-req">*</span>
                    <select className="reg-input" value={form.purpose} onChange={set('purpose')} required>
                      <option value="">Select the type of letter that you need</option>
                      {PURPOSES.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </label>
                  <label>Name and Address of Organization the Letter is being sent to <span className="reg-req">*</span>
                    <textarea className="reg-input" rows={3} value={form.organization} onChange={set('organization')}
                      placeholder="Format: Company/Organization Name, Address (St, City, State, ZIP, Country)" required />
                  </label>
                  <label>Name and Title of Person the letter is being sent to <span className="reg-req">*</span>
                    <input type="text" className="reg-input" value={form.recipient_person} onChange={set('recipient_person')}
                      placeholder="Put N/A if not applicable" required />
                  </label>
                  <label>Name and Dates of Event the letter should reference <span className="reg-req">*</span>
                    <textarea className="reg-input" rows={2} value={form.event_details} onChange={set('event_details')}
                      placeholder="For example: FIBA 3x3 Americup Senior Tournament, December 10-16, 2024" required />
                  </label>
                </div>
              </section>
              <button type="submit" className="btn-primary reg-submit" disabled={sending}>
                <Send size={16} /> {sending ? 'Submitting...' : 'Submit Letter Request'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
